import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../config/api';
import useAuthStore from '../store/authStore';

const GREEN = '#2e9f86';

export default function Signup() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [form, setForm] = useState({ company_name: '', name: '', email: '', password: '' });
  const [step, setStep] = useState(1); // 1 = details, 2 = verify code
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === 2 && codeRef.current) codeRef.current.focus();
  }, [step]);

  // create the account (codeStr empty on local/offline installs where verification is disabled)
  const createAccount = async (codeStr) => {
    const res = await api.post('/api/signup', { ...form, code: codeStr });
    const { token, user } = res.data;
    if (res.data.tenant_hint) localStorage.setItem('tenant_hint', res.data.tenant_hint);
    login(token, user || { name: form.name || form.company_name, email: form.email, role: 'admin' });
    navigate('/'); // enters the app; SetupWizard runs because setup_completed:false
  };

  // Step 1: validate form, send verification code
  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setInfo('');
    if (!form.company_name || !form.email || !form.password) {
      setError('Please fill company name, email and password.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/signup/request-code', { email: form.email });
      if (res.data?.verification_disabled) {
        // local/offline install — no email step, create the account directly
        await createAccount('');
        return;
      }
      setStep(2);
      setCode('');
      setCooldown(60);
      setInfo(`We sent a 6-digit code to ${form.email.trim()}.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: verify code + create account
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      await createAccount(code.trim());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create account. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 9, padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#0f172a 0%,#134e4a 100%)', padding: 20, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ width: 420, maxWidth: '100%', background: '#fff', borderRadius: 18, boxShadow: '0 24px 64px rgba(0,0,0,0.35)', padding: 32 }}>
        {/* logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg,${GREEN},#1f7d68)`, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 3, padding: 10 }}>
            <span style={{ width: 4, height: 9, background: 'rgba(255,255,255,.9)', borderRadius: 2 }} />
            <span style={{ width: 4, height: 14, background: '#fff', borderRadius: 2 }} />
            <span style={{ width: 4, height: 18, background: 'rgba(255,255,255,.9)', borderRadius: 2 }} />
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0d0d0d' }}>MMM<span style={{ color: GREEN }}>POS</span></div>
        </div>

        {step === 1 ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginTop: 14 }}>Start your free trial</div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>7 days free — no card required.</div>

            <form onSubmit={requestCode}>
              {[
                { k: 'company_name', label: 'Company name', type: 'text', ph: 'My Shop' },
                { k: 'name', label: 'Your name', type: 'text', ph: 'Owner name' },
                { k: 'email', label: 'Email', type: 'email', ph: 'you@shop.com' },
                { k: 'password', label: 'Password', type: 'password', ph: 'At least 6 characters' },
              ].map((f) => (
                <div key={f.k} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>{f.label}</label>
                  <input
                    type={f.type}
                    value={form[f.k]}
                    onChange={(e) => set(f.k, e.target.value)}
                    placeholder={f.ph}
                    style={inputStyle}
                  />
                </div>
              ))}

              {error && <div style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}

              <button type="submit" disabled={loading} style={{ width: '100%', background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
                {loading ? 'Sending code…' : 'Continue'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginTop: 14 }}>Check your email</div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>{info || `We sent a 6-digit code to ${form.email}.`}</div>

            <form onSubmit={submit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>Verification code</label>
                <input
                  ref={codeRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  style={{ ...inputStyle, fontSize: 22, letterSpacing: 8, textAlign: 'center', fontWeight: 700 }}
                />
              </div>

              {error && <div style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}

              <button type="submit" disabled={loading || code.length !== 6} style={{ width: '100%', background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: loading || code.length !== 6 ? 0.6 : 1 }}>
                {loading ? 'Creating your shop…' : 'Verify & create account'}
              </button>
            </form>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 13 }}>
              <button type="button" onClick={() => { setStep(1); setError(''); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, fontSize: 13 }}>
                ← Edit details
              </button>
              <button
                type="button"
                onClick={requestCode}
                disabled={cooldown > 0 || loading}
                style={{ background: 'none', border: 'none', color: cooldown > 0 ? '#9ca3af' : GREEN, cursor: cooldown > 0 ? 'default' : 'pointer', padding: 0, fontSize: 13, fontWeight: 600 }}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6b7280' }}>
          Already have an account? <Link to="/login" style={{ color: GREEN, fontWeight: 600, textDecoration: 'none' }}>Log in</Link>
        </div>
      </div>
    </div>
  );
}
